import {
  Box,
  Checkbox,
  CheckboxGroup,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Stack,
  StackProps,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { HANDLING_OPTIONS, PACKING_OPTIONS, Show } from '@pelicargo/ui';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useSimpleFormContext } from './SimpleForm';

type Props = StackProps & {
  handlingName: string;
  packingName: string;
  label: string;
  isHighlighted?: boolean;
};

const options = [...HANDLING_OPTIONS, ...PACKING_OPTIONS];

export const SimpleHandlingOptions = ({ handlingName, packingName, label, isHighlighted, ...rest }: Props) => {
  const handling = useWatch({ name: handlingName });
  const packing = useWatch({ name: packingName });
  const { updateField } = useSimpleFormContext();
  const { setValue, getValues } = useFormContext();

  const selectedOptions = useMemo(() => {
    return [handling, ...(packing || [])];
  }, [handling, packing]);

  const handleUpdate = useCallback(async () => {
    const values = getValues();

    updateField('handling', [values?.handling, ...(values?.packing || [])]);
  }, [getValues, updateField]);

  const onRadioChange = useCallback(
    (nextValue: string) => {
      setValue(handlingName, nextValue);
      handleUpdate();
    },
    [handleUpdate, handlingName, setValue],
  );

  const onCheckboxChange = useCallback(
    (nextValue: string[]) => {
      setValue(packingName, nextValue);
      handleUpdate();
    },
    [handleUpdate, packingName, setValue],
  );

  const renderHandlingOption = useCallback(({ label, value, icon }) => {
    return (
      <Radio key={value} value={value} size="lg">
        <Stack direction="row" align="center">
          <Icon as={icon} w="20px" h="20px" color="white" />
          <Text>{label}</Text>
        </Stack>
      </Radio>
    );
  }, []);

  const renderPackingOption = useCallback(({ label, value, icon }) => {
    return (
      <Checkbox key={value} value={value} size="lg">
        <Stack direction="row" align="center">
          <Icon as={icon} w="20px" h="20px" color="white" />
          <Text>{label}</Text>
        </Stack>
      </Checkbox>
    );
  }, []);

  const renderSelectedOption = useCallback((option: string | number) => {
    const selected = options.find((opt) => opt.value === option);

    return (
      <Box key={selected?.value} h="full">
        <Tooltip label={selected?.label}>
          <Stack direction="row" align="center">
            <Icon as={selected?.icon} w="20px" h="20px" color="white" />
          </Stack>
        </Tooltip>
      </Box>
    );
  }, []);

  const hoverBorderColor = useColorModeValue('gray.100', 'gray.700');
  const borderColor = isHighlighted ? 'error' : 'transparent';

  return (
    <Popover>
      <PopoverTrigger>
        <Stack
          spacing={1}
          py={1}
          px={2}
          borderRadius={8}
          borderWidth={1}
          cursor="default"
          h="full"
          borderColor={borderColor}
          _hover={{ borderColor: hoverBorderColor }}
          {...rest}
        >
          <Text fontSize="xs" fontWeight="400">
            {label}
          </Text>
          <Stack direction="row" alignItems="center">
            <Show if={!selectedOptions?.length}>
              <Text fontSize="md" color="gray.400">
                None selected
              </Text>
            </Show>
            {selectedOptions?.map(renderSelectedOption)}
          </Stack>
        </Stack>
      </PopoverTrigger>
      <PopoverContent p="2" w="full" border="none" shadow="lg">
        <Stack spacing="4">
          <Stack>
            <Text textStyle="caption">Handling</Text>
            <RadioGroup colorScheme="blue" value={handling} onChange={onRadioChange}>
              <Stack spacing="2">{HANDLING_OPTIONS.map(renderHandlingOption)}</Stack>
            </RadioGroup>
          </Stack>
          <Stack>
            <Text textStyle="caption">Packing</Text>
            <CheckboxGroup colorScheme="blue" value={packing} onChange={onCheckboxChange}>
              {PACKING_OPTIONS.map(renderPackingOption)}
            </CheckboxGroup>
          </Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
